import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useSupabaseContext } from "../lib/supabase_context";
import { useToast } from "../hooks/use-toast";
import { CheckCircle, X } from "lucide-react";
import Button from "../components/theme/Button";

export interface favoriteItems {
  id: number;
  product_id: string;
  storeName: string;
  store_id: number;
  name: string;
  image: string;
  price: number;
  rating: number;
  status: boolean;
}

interface FavoritesContextType {
  favorites: favoriteItems[];
  addToFavorites: (productId: string) => void;
  removeFromFavorites: (productId: string) => void;
  openFavoriteLoginPrompt: () => void;
}

const FavoriteContext = createContext<FavoritesContextType | undefined>(
  undefined
);

const fetchFavoritesAPI = async (
  action: string,
  payload?: { productId?: string }
) => {
  try {
    const res = await fetch("/api/favorite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, payload }),
    });

    return await res.json();
  } catch (err) {
    console.error("Favorites API error:", err);
    return { error: true };
  }
};

export const FavoriteProvider = ({ children }: { children: ReactNode }) => {
  const { supabase, session } = useSupabaseContext();
  const [user, setUser] = useState(session?.user ?? null);
  const [favorites, setFavorites] = useState<favoriteItems[]>([]);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const { toast } = useToast();

  const userId = user?.id;

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );
    return () => listener.subscription.unsubscribe();
  }, [supabase]);

  const loadFavorites = useCallback(async () => {
    if (!userId) {
      setFavorites([]);
      return;
    }

    const data = await fetchFavoritesAPI("load");

    if (!data || data.error) {
      toast({
        title: "Error",
        description: "Could not load favorites.",
        variant: "destructive",
      });
      return;
    }
    setFavorites(data.items ?? []);
  }, [userId, toast]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const openFavoriteLoginPrompt = useCallback(() => {
    setLoginPromptOpen(true);
  }, []);

  const addToFavorites = useCallback(
    async (productId: string) => {
      if (!userId) {
        setLoginPromptOpen(true);
        return;
      }

      const data = await fetchFavoritesAPI("add", { productId });

      if (data?.error) {
        toast({
          title: "Error",
          description: "Could not add favorite.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Added",
        variant: "success",
        description: "Product added to favorites",
        action: <CheckCircle className="h-4 w-4 text-green-500" />,
      });

      await loadFavorites();
    },
    [userId, loadFavorites, toast]
  );

  const removeFromFavorites = useCallback(
    async (productId: string) => {
      if (!userId) {
        setLoginPromptOpen(true);
        return;
      }

      const data = await fetchFavoritesAPI("remove", { productId });

      if (data?.error) {
        toast({
          description: "Error removing favorite.",
          variant: "destructive",
        });
        return;
      }
      
       setFavorites((prev) =>
        prev.filter((item) => item.product_id !== productId)
      );

      toast({
        title: "Removed",
        variant: "success",
        description: "Product removed from favorites",
        action: <CheckCircle className="h-4 w-4 text-green-500" />,
      });

    },
    [userId, toast]
  );

  const value = useMemo(
    () => ({
      favorites,
      addToFavorites,
      removeFromFavorites,
      openFavoriteLoginPrompt,
    }),
    [favorites, addToFavorites, removeFromFavorites, openFavoriteLoginPrompt]
  );

  return (
    <FavoriteContext.Provider value={value}>
      {children}
      {loginPromptOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close dialog"
            onClick={() => setLoginPromptOpen(false)}
          />
          <div
            className="relative z-10 w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900"
            role="dialog"
            aria-modal="true"
            aria-labelledby="favorites-login-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2
                id="favorites-login-title"
                className="text-xl font-semibold text-slate-800 dark:text-slate-100"
              >
                Login required
              </h2>
              <button
                type="button"
                onClick={() => setLoginPromptOpen(false)}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">
              Sign in to save products to your favorites and access them from
              any device.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="rounded-md bg-slate-200 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                onClick={() => setLoginPromptOpen(false)}
              >
                Cancel
              </button>
              <Button
                mode="primary"
                onClick={() => {
                  window.location.href = "/login";
                }}
              >
                Log in
              </Button>
            </div>
          </div>
        </div>
      )}
    </FavoriteContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoriteContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoriteProvider");
  }
  return context;
};